/**
 * The authenticated frame. Linear/Attio pattern: a light collapsible rail with sections,
 * a top bar (breadcrumb, branch, ⌘K, one primary action, bell, avatar menu), and on a
 * phone a bottom tab bar with the rest under "Yana".
 */
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useState, type JSX } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router";
import {
  ChevronsLeft,
  ChevronsRight,
  CreditCard,
  Ellipsis,
  Plus,
  Search,
  Users,
  Briefcase,
  Wallet,
  type LucideIcon,
} from "lucide-react";

import { coreApi } from "@/api/endpoints";
import { queryKeys } from "@/api/queryKeys";
import type { Role } from "@/api/types";
import { useTranslation } from "@/i18n";
import { cn } from "@/lib/cn";
import { showsSuspendedScreen } from "@/lib/support";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/ui/button";
import type { PaletteGroup } from "@/ui/command-palette";

// Loaded on the first ⌘K, so the command library stays out of the first paint.
const CommandPalette = lazy(async () => {
  const module = await import("@/ui/command-palette");
  return { default: module.CommandPalette };
});
import { DropdownMenu, type MenuItem } from "@/ui/dropdown-menu";
import { IconButton } from "@/ui/icon-button";
import { fieldClass } from "@/ui/input";
import { Sheet } from "@/ui/sheet";
// Loaded with the first paint, but outside the entry chunk: sonner is only needed once
// something can be toasted, and nothing in the shell imports it statically.
const Toaster = lazy(async () => {
  const module = await import("@/ui/toast");
  return { default: module.Toaster };
});
import { Tooltip } from "@/ui/tooltip";
import { useRealtimeSync } from "@/app/useRealtimeSync";

import { NotificationBell } from "./NotificationBell";
import { UserMenu } from "./UserMenu";
import { useApplyPreferences } from "@/features/profile/preferences";
import { SupportBanner } from "./SupportBanner";
import { SuspendedScreen } from "./SuspendedScreen";
import { mobileTabs, navItemsForGroup, visibleNavItems, type NavGroup, type NavItem } from "./navigation";
import { useNavCounts } from "./useNavCounts";
import { useScopeStore } from "./scope";
import { locksViewport } from "./viewportLock";

/** The route whose nav item names the page, for the breadcrumb and the active state. */
function activeNavItem(pathname: string, role: Role | undefined): NavItem | undefined {
  if (role === undefined) return undefined;
  return visibleNavItems(role).find(
    (item) => item.to !== null && (item.to === "/" ? pathname === "/" : pathname === item.to || pathname.startsWith(`${item.to}/`)),
  );
}

function NavRow({
  item,
  collapsed,
  onNavigate,
  count,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: (() => void) | undefined;
  count?: number | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  const Icon: LucideIcon = item.icon;
  const label = t(item.labelKey);
  // A number is shown only when there is something to act on, and only in the expanded rail.
  const badge =
    collapsed || count === undefined || count === 0 ? null : (
      <span
        aria-label={t("nav.badgeCount", { count: String(count) })}
        className="ml-auto rounded-pill bg-accent-soft px-1.5 text-xs font-medium text-accent-ink tabular-nums"
      >
        {count}
      </span>
    );
  const base = cn(
    "flex h-8 items-center gap-2.5 rounded-small px-2 text-base whitespace-nowrap transition-colors duration-fast ease-token [&_svg]:size-4 [&_svg]:shrink-0",
    collapsed && "justify-center px-0",
  );

  if (item.to === null) {
    const row = (
      <span
        aria-disabled="true"
        title={t("nav.soon")}
        className={cn(base, "cursor-default text-sidebar-ink/60 [&_svg]:text-sidebar-ink/60")}
      >
        <Icon strokeWidth={1.5} aria-hidden="true" />
        {collapsed ? <span className="sr-only">{label}</span> : <span className="truncate">{label}</span>}
      </span>
    );
    return <li>{collapsed ? <Tooltip content={label}>{row}</Tooltip> : row}</li>;
  }

  const link = (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      aria-label={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          base,
          "border",
          isActive
            ? "border-line bg-surface font-medium text-sidebar-ink-active [&_svg]:text-accent-ink"
            : "border-transparent text-sidebar-ink hover:bg-sidebar-hover hover:text-sidebar-ink-active [&_svg]:text-ink-subtle",
        )
      }
    >
      <Icon strokeWidth={1.5} aria-hidden="true" />
      {collapsed ? null : <span className="truncate">{label}</span>}
      {badge}
    </NavLink>
  );
  return <li>{collapsed ? <Tooltip content={label}>{link}</Tooltip> : link}</li>;
}

function NavGroupBlock({ group, collapsed, onNavigate }: { group: NavGroup; collapsed: boolean; onNavigate?: (() => void) | undefined }): JSX.Element | null {
  const { t } = useTranslation();
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  // Called before any early return: a hook after one would change order between renders.
  const counts = useNavCounts();
  if (role === undefined) return null;
  const items = navItemsForGroup(role, group);
  if (items.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      {collapsed ? null : (
        <span className="px-2 pt-2 text-xs font-medium text-ink-subtle">
          {t(group === "main" ? "nav.group.main" : "nav.group.management")}
        </span>
      )}
      <ul className="flex flex-col gap-0.5">
        {items.map((item) => (
          <NavRow
            key={item.key}
            item={item}
            collapsed={collapsed}
            onNavigate={onNavigate}
            count={item.countKey === undefined ? undefined : counts[item.countKey]}
          />
        ))}
      </ul>
    </div>
  );
}

function Rail({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }): JSX.Element {
  const { t } = useTranslation();
  const tenant = useAuthStore((state) => state.user?.tenant);
  return (
    <aside
      className={cn(
        "sticky top-0 hidden h-dvh shrink-0 flex-col justify-between border-r border-line bg-sidebar py-3 md:flex",
        collapsed ? "w-14 px-2" : "w-[var(--k-sidebar-width)] px-3",
      )}
    >
      <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
        <div className={cn("flex items-center gap-2", collapsed && "justify-center")}>
          <span className="flex size-7 shrink-0 items-center justify-center rounded-small bg-btn-primary text-sm font-semibold text-btn-primary-ink">K</span>
          {collapsed ? null : (
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="text-base font-semibold text-sidebar-ink-active">{t("app.name")}</span>
              {tenant === undefined || tenant === null ? null : <span className="truncate text-xs text-ink-subtle">{tenant.name}</span>}
            </span>
          )}
        </div>
        <nav aria-label={t("app.name")} className="flex flex-col gap-3">
          <NavGroupBlock group="main" collapsed={collapsed} />
          <NavGroupBlock group="management" collapsed={collapsed} />
        </nav>
      </div>
      <IconButton
        label={collapsed ? t("shell.expand") : t("shell.collapse")}
        variant="ghost"
        onClick={onToggle}
        className={cn("text-ink-subtle", collapsed ? "mx-auto" : "self-start")}
      >
        {collapsed ? <ChevronsRight strokeWidth={1.5} aria-hidden="true" /> : <ChevronsLeft strokeWidth={1.5} aria-hidden="true" />}
      </IconButton>
    </aside>
  );
}

function BranchPicker(): JSX.Element | null {
  const { t } = useTranslation();
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  const branchId = useScopeStore((state) => state.branchId);
  const setBranchId = useScopeStore((state) => state.setBranchId);
  // Only roles that span more than their own branch get the picker.
  const enabled = role === "owner" || role === "moliyachi";
  const branches = useQuery({
    queryKey: queryKeys.branches(),
    queryFn: () => coreApi.branches(),
    enabled,
    staleTime: 5 * 60_000,
  });
  if (!enabled || branches.data === undefined) return null;

  // A native select: the branch list is short and the browser's own control is the
  // most accessible option, and it keeps a Radix popper out of the first paint.
  return (
    <div className="hidden w-44 lg:block">
      <select
        aria-label={t("shell.branch")}
        value={branchId === null ? "all" : String(branchId)}
        onChange={(event) => {
          setBranchId(event.target.value === "all" ? null : Number(event.target.value));
        }}
        className={fieldClass}
      >
        <option value="all">{t("common.allBranches")}</option>
        {branches.data.map((branch) => (
          <option key={branch.id} value={String(branch.id)}>
            {branch.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function CreateMenu(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const items: readonly MenuItem[] = [
    { key: "deal", label: t("shell.create.deal"), icon: <Briefcase strokeWidth={1.5} />, onSelect: (): void => { void navigate("/bitimlar"); } },
    { key: "client", label: t("shell.create.client"), icon: <Users strokeWidth={1.5} />, onSelect: (): void => { void navigate("/mijozlar/yangi"); } },
    { key: "payment", label: t("shell.create.payment"), icon: <Wallet strokeWidth={1.5} />, onSelect: (): void => { void navigate("/moliya"); } },
    { key: "expense", label: t("shell.create.expense"), icon: <CreditCard strokeWidth={1.5} />, onSelect: (): void => { void navigate("/moliya"); } },
  ];
  return (
    <>
      <span className="hidden md:inline-flex">
        <DropdownMenu
          label={t("common.create")}
          trigger={
            <Button size="md" tone="ink">{t("common.create")}</Button>
          }
          items={items}
          align="end"
        />
      </span>
      <span className="md:hidden">
        <DropdownMenu
          label={t("common.create")}
          trigger={
            <IconButton label={t("common.create")} variant="primary" tone="ink">
              <Plus strokeWidth={1.5} aria-hidden="true" />
            </IconButton>
          }
          items={items}
          align="end"
        />
      </span>
    </>
  );
}

function TopBar({ onOpenPalette, title }: { onOpenPalette: () => void; title: string | null }): JSX.Element {
  const { t } = useTranslation();
  return (
    <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-3 border-b border-line bg-canvas px-4">
      <nav aria-label={t("shell.breadcrumbLabel")} className="flex min-w-0 flex-1 items-center gap-2 text-base">
        {title === null ? null : <span className="truncate font-medium text-ink">{title}</span>}
      </nav>

      <Button
        variant="secondary"
        size="sm"
        className="hidden w-72 min-w-32 shrink justify-between font-normal text-ink-subtle sm:inline-flex"
        onClick={onOpenPalette}
      >
        <span className="flex min-w-0 items-center gap-2">
          <Search className="shrink-0" strokeWidth={1.5} aria-hidden="true" />
          <span className="truncate">{t("shell.searchPlaceholder")}</span>
        </span>
        <kbd className="shrink-0 rounded-small border border-line px-1 font-mono text-xs text-ink-subtle">⌘K</kbd>
      </Button>
      <IconButton label={t("shell.search")} variant="ghost" className="sm:hidden" onClick={onOpenPalette}>
        <Search strokeWidth={1.5} aria-hidden="true" />
      </IconButton>

      <BranchPicker />
      <CreateMenu />
      <NotificationBell />
      <UserMenu />
    </header>
  );
}

function MobileTabBar({ onMore }: { onMore: () => void }): JSX.Element | null {
  const { t } = useTranslation();
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  if (role === undefined) return null;
  const { primary, overflow } = mobileTabs(role);
  return (
    <nav
      aria-label={t("app.name")}
      className="fixed inset-x-0 bottom-0 z-30 flex h-[var(--k-tabbar-height)] items-stretch border-t border-line bg-surface md:hidden"
    >
      {primary.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.key}
            to={item.to ?? "/"}
            className={({ isActive }) =>
              cn(
                "flex flex-1 flex-col items-center justify-center gap-1 text-xs",
                isActive ? "text-accent-ink" : "text-ink-subtle",
              )
            }
          >
            <Icon strokeWidth={1.5} className="size-5" aria-hidden="true" />
            <span className="truncate">{t(item.labelKey)}</span>
          </NavLink>
        );
      })}
      {overflow.length === 0 ? null : (
        <button
          type="button"
          onClick={onMore}
          className="flex flex-1 flex-col items-center justify-center gap-1 text-xs text-ink-subtle"
        >
          <Ellipsis strokeWidth={1.5} className="size-5" aria-hidden="true" />
          <span>{t("nav.more")}</span>
        </button>
      )}
    </nav>
  );
}

function MoreSheet({ open, onOpenChange, onNavigate }: { open: boolean; onOpenChange: (open: boolean) => void; onNavigate: () => void }): JSX.Element | null {
  const { t } = useTranslation();
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  if (role === undefined) return null;
  const { overflow } = mobileTabs(role);
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title={t("shell.mobileMore")}>
      <ul className="flex flex-col gap-1">
        {overflow.map((item) => (
          <NavRow key={item.key} item={item} collapsed={false} onNavigate={onNavigate} />
        ))}
      </ul>
    </Sheet>
  );
}

export function AppShell(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  const tenantStatus = useAuthStore((state) => state.user?.tenant?.status);
  const suspended = showsSuspendedScreen(tenantStatus, role, location.pathname);
  const locked = !suspended && locksViewport(location.pathname);
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // One live socket for the whole signed-in shell (D88); frames refresh whatever is open.
  useRealtimeSync();
  useApplyPreferences();

  // ⌘K and Ctrl+K open the palette from anywhere in the shell.
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    }
    window.addEventListener("keydown", onKey);
    return (): void => {
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const current = activeNavItem(location.pathname, role);
  const title = current === undefined ? null : t(current.labelKey);

  const groups: readonly PaletteGroup[] = role === undefined ? [] : [
    {
      heading: t("shell.palette.navigate"),
      items: visibleNavItems(role)
        .filter((item) => item.to !== null)
        .map((item) => ({
          key: item.key,
          label: t(item.labelKey),
          icon: <item.icon strokeWidth={1.5} aria-hidden="true" />,
          onSelect: (): void => {
            if (item.to !== null) void navigate(item.to);
          },
        })),
    },
  ];

  return (
    <div className={cn("flex min-h-dvh bg-canvas text-ink", locked && "lg:h-dvh lg:min-h-0 lg:overflow-hidden")}>
      <Rail collapsed={collapsed} onToggle={() => { setCollapsed((value) => !value); }} />
      <div className={cn("flex min-w-0 flex-1 flex-col pb-[var(--k-tabbar-height)] md:pb-0", locked && "lg:min-h-0")}>
        <SupportBanner />
        <TopBar onOpenPalette={() => { setPaletteOpen(true); }} title={title} />
        <main className={cn("min-w-0 flex-1 px-4 py-4 md:px-6", locked && "lg:flex lg:min-h-0 lg:flex-col lg:overflow-hidden")}>
          {suspended ? <SuspendedScreen /> : <Outlet />}
        </main>
      </div>
      <MobileTabBar onMore={() => { setMoreOpen(true); }} />
      <MoreSheet open={moreOpen} onOpenChange={setMoreOpen} onNavigate={() => { setMoreOpen(false); }} />
      {!paletteOpen ? null : (
        <Suspense fallback={null}>
          <CommandPalette
            open={paletteOpen}
            onOpenChange={setPaletteOpen}
            title={t("shell.palette.title")}
            placeholder={t("shell.palette.placeholder")}
            emptyLabel={t("shell.palette.empty")}
            groups={groups}
          />
        </Suspense>
      )}
      <Suspense fallback={null}>
        <Toaster />
      </Suspense>
    </div>
  );
}

